<template>
    <PageHeader :title="$t('game_stats.title')" @back="goBack" />

    <div v-if="error" class="flex min-h-64 items-center justify-center">
        <div class="text-center text-lg text-red-500">
            <div class="mb-4">{{ error }}</div>
            <button @click="fetchMatchData" class="rounded bg-red-600 px-4 py-2 text-white">
                {{ $t('game_stats.try_again') }}
            </button>
        </div>
    </div>

    <div v-else class="flex-1 p-4">
        <div v-if="teams.length === 2" class="mb-8 flex items-stretch">
            <template v-for="(team, index) in teams" :key="index">
                <div v-if="index > 0" class="border-stroke-1 mx-2 w-px border-l"></div>

                <div class="relative flex-1 text-center">
                    <h3
                        v-if="!loading && isTeamGame"
                        class="font-title mb-4 text-sm font-extrabold text-white uppercase"
                    >
                        {{ $t('game_stats.team_label', { n: index + 1 }) }}
                    </h3>
                    <div v-if="loading" class="animate-pulse">
                        <div class="mx-auto mb-2 h-16 w-16 rounded-full bg-zinc-700"></div>
                        <div class="mx-auto h-4 w-24 rounded bg-zinc-700"></div>
                    </div>
                    <template v-else>
                        <div
                            v-if="isWinner(team[0]?.team)"
                            class="absolute -top-2 right-2 z-10 text-2xl"
                        >
                            🏆
                        </div>
                        <div
                            v-for="player in team"
                            :key="player.id"
                            class="mb-4 flex items-center justify-center"
                        >
                            <Avatar :size="16" :src="player.avatar" :alt="player.tag_name" />
                            <div class="ml-1 flex items-center justify-center">
                                <span
                                    class="line-clamp-1 block max-w-20 truncate font-semibold text-white"
                                    >{{ player.tag_name }}</span
                                >
                            </div>
                        </div>
                    </template>
                </div>
            </template>
        </div>

        <div v-if="loading" class="space-y-8">
            <div class="animate-pulse space-y-4">
                <div v-for="i in 2" :key="i" class="flex justify-between">
                    <div class="h-6 w-24 rounded bg-zinc-700"></div>
                    <div class="flex gap-8">
                        <div class="h-6 w-12 rounded bg-zinc-700"></div>
                        <div class="h-6 w-12 rounded bg-zinc-700"></div>
                    </div>
                </div>
            </div>
            <div class="animate-pulse space-y-4">
                <div v-for="i in 4" :key="i" class="flex justify-between">
                    <div class="h-6 w-24 rounded bg-zinc-700"></div>
                    <div class="flex gap-8">
                        <div class="h-6 w-12 rounded bg-zinc-700"></div>
                        <div class="h-6 w-12 rounded bg-zinc-700"></div>
                    </div>
                </div>
            </div>
        </div>

        <template v-else-if="matchData">
            <div class="space-y-8">
                <template v-if="teams.length <= 2 && !isKillerGame">
                    <div v-if="!isSegmentsGame" class="space-y-1">
                        <StatRow
                            :label="$t('game_stats.sets_won')"
                            :player1-value="getStatValue(teams[0][0]?.team, 'sets_won')"
                            :player2-value="
                                teams.length === 2
                                    ? getStatValue(teams[1][0]?.team, 'sets_won')
                                    : null
                            "
                            :isSoloGame="isSoloGame"
                        />
                        <StatRow
                            :label="$t('game_stats.legs_won')"
                            :player1-value="getStatValue(teams[0][0]?.team, 'legs_won')"
                            :player2-value="
                                teams.length === 2
                                    ? getStatValue(teams[1][0]?.team, 'legs_won')
                                    : null
                            "
                            :isSoloGame="isSoloGame"
                        />
                    </div>

                    <div v-if="isX01Game" class="space-y-1">
                        <StatRow
                            v-for="(stat, index) in x01Stats"
                            :key="index"
                            :label="stat.label"
                            :lower-is-better="stat.lowerIsBetter"
                            :no-highlight="stat.noHighlight"
                            :player1-value="
                                formatX01Stat(
                                    getStatValue(teams[0][0]?.team, stat.key),
                                    stat,
                                    teams[0][0]?.team,
                                )
                            "
                            :player2-value="
                                teams.length === 2
                                    ? formatX01Stat(
                                          getStatValue(teams[1][0]?.team, stat.key),
                                          stat,
                                          teams[1][0]?.team,
                                      )
                                    : null
                            "
                            :isSoloGame="isSoloGame"
                        />
                    </div>

                    <div v-else-if="isSegmentsGame" class="space-y-1">
                        <StatRow
                            v-for="(stat, index) in segmentsStats"
                            :key="index"
                            :label="stat.label"
                            :lower-is-better="stat.lowerIsBetter"
                            :no-highlight="stat.noHighlight"
                            :player1-value="
                                formatSegmentsStat(
                                    getStatValue(teams[0][0]?.team, stat.key),
                                    stat,
                                    teams[0][0]?.team,
                                )
                            "
                            :player2-value="
                                teams.length === 2
                                    ? formatSegmentsStat(
                                          getStatValue(teams[1][0]?.team, stat.key),
                                          stat,
                                          teams[1][0]?.team,
                                      )
                                    : null
                            "
                            :isSoloGame="isSoloGame"
                        />
                    </div>

                    <div v-else class="space-y-1">
                        <template v-for="(stat, index) in availableStats" :key="index">
                            <div v-if="stat.isSpacer" class="h-10"></div>
                            <StatRow
                                v-else
                                :label="stat.label"
                                :lower-is-better="stat.lowerIsBetter"
                                :no-highlight="stat.noHighlight"
                                :player1-value="
                                    stat.key === 'double_percentage'
                                        ? formatDoublePercentage(
                                              getStatValue(teams[0][0]?.team, stat.key),
                                          )
                                        : getStatValue(teams[0][0]?.team, stat.key)
                                "
                                :player2-value="
                                    teams.length === 2
                                        ? stat.key === 'double_percentage'
                                            ? formatDoublePercentage(
                                                  getStatValue(teams[1][0]?.team, stat.key),
                                              )
                                            : getStatValue(teams[1][0]?.team, stat.key)
                                        : null
                                "
                                :isSoloGame="isSoloGame"
                            />
                        </template>
                    </div>
                </template>

                <template v-else>
                    <div class="overflow-x-auto">
                        <table class="w-max min-w-full table-auto">
                            <thead>
                                <tr>
                                    <th class="w-auto px-4 py-2 text-left font-bold"></th>
                                    <th
                                        v-for="(team, index) in teams"
                                        :key="`header-${index}`"
                                        class="w-28 px-3 py-2 text-center"
                                    >
                                        <div class="relative flex flex-col items-center gap-1">
                                            <div
                                                v-if="isWinner(team[0]?.team)"
                                                class="absolute -top-1 -right-1 z-10 text-xl"
                                            >
                                                🏆
                                            </div>
                                            <Avatar
                                                :size="12"
                                                :src="team[0]?.avatar"
                                                :alt="team[0]?.tag_name"
                                            />
                                            <span class="w-full truncate text-xs text-white">{{
                                                team[0]?.tag_name
                                            }}</span>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-if="isKillerGame">
                                    <tr class="even:bg-card-2">
                                        <td class="px-4 py-2 text-left font-bold">{{ $t('game_stats.lives') }}</td>
                                        <td
                                            v-for="(team, index) in teams"
                                            :key="`lives-${index}`"
                                            class="w-28 px-3 py-2 text-center text-lg font-bold text-white"
                                        >
                                            {{ killerStats[team[0]?.team]?.lives ?? 0 }}
                                        </td>
                                    </tr>
                                    <tr class="even:bg-card-2">
                                        <td class="px-4 py-2 text-left font-bold">{{ $t('game_stats.status') }}</td>
                                        <td
                                            v-for="(team, index) in teams"
                                            :key="`status-${index}`"
                                            class="w-28 px-3 py-2 text-center text-sm font-bold"
                                        >
                                            <span
                                                v-if="killerStats[team[0]?.team]?.eliminated"
                                                class="text-red-500"
                                                >{{ $t('game_stats.eliminated') }}</span
                                            >
                                            <span
                                                v-else-if="killerStats[team[0]?.team]?.isKiller"
                                                class="text-green-500"
                                                >{{ $t('game_stats.killer') }}</span
                                            >
                                            <span v-else class="text-gray-400">{{ $t('game_stats.active') }}</span>
                                        </td>
                                    </tr>
                                </template>

                                <template v-else-if="!isSegmentsGame">
                                    <tr class="even:bg-card-2">
                                        <td class="px-4 py-2 text-left font-bold">{{ $t('game_stats.sets_won') }}</td>
                                        <td
                                            v-for="(team, index) in teams"
                                            :key="`sets-${index}`"
                                            class="w-28 px-3 py-2 text-center text-lg font-bold text-white"
                                        >
                                            {{ getStatValue(team[0]?.team, 'sets_won') }}
                                        </td>
                                    </tr>
                                    <tr class="even:bg-card-2">
                                        <td class="px-4 py-2 text-left font-bold">{{ $t('game_stats.legs_won') }}</td>
                                        <td
                                            v-for="(team, index) in teams"
                                            :key="`legs-${index}`"
                                            class="w-28 px-3 py-2 text-center text-lg font-bold text-white"
                                        >
                                            {{ getStatValue(team[0]?.team, 'legs_won') }}
                                        </td>
                                    </tr>
                                </template>
                                <template
                                    v-for="(stat, index) in tableStats"
                                    :key="`${stat.key}-${index}`"
                                >
                                    <tr v-if="stat.isSpacer">
                                        <td :colspan="teams.length + 1" class="h-10"></td>
                                    </tr>
                                    <tr v-else class="even:bg-card-2">
                                        <td class="px-4 py-2 text-left font-bold whitespace-nowrap">
                                            {{ stat.label }}
                                        </td>
                                        <td
                                            v-for="(team, tIndex) in teams"
                                            :key="`${stat.key}-${tIndex}`"
                                            class="w-28 shrink-0 px-3 py-2 text-center text-lg font-bold whitespace-nowrap text-white"
                                        >
                                            <template v-if="isX01Game">
                                                {{
                                                    formatX01Stat(
                                                        getStatValue(team[0]?.team, stat.key),
                                                        stat,
                                                        team[0]?.team,
                                                    ) ?? '-'
                                                }}
                                            </template>
                                            <template v-else-if="isSegmentsGame">
                                                {{
                                                    formatSegmentsStat(
                                                        getStatValue(team[0]?.team, stat.key),
                                                        stat,
                                                        team[0]?.team,
                                                    ) ?? '-'
                                                }}
                                            </template>
                                            <template v-else>
                                                {{
                                                    stat.key === 'double_percentage'
                                                        ? formatDoublePercentage(
                                                              getStatValue(team[0]?.team, stat.key),
                                                          )
                                                        : getStatValue(team[0]?.team, stat.key)
                                                }}
                                            </template>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </template>
            </div>
        </template>
    </div>

    <div
        v-if="matchData"
        class="border-stroke-1 bg-card mt-4 border-t p-4"
        :class="[canEndMatch ? 'grid grid-cols-2 gap-4' : '']"
    >
        <DiagonalCutButton
            v-if="canEndMatch"
            @click="rematch"
            variant="secondary"
            cut-position="right-top"
            :loading="isRematchLoading"
            :disabled="isRematchLoading"
        >
            {{ $t('game_stats.rematch') }}
        </DiagonalCutButton>
        <DiagonalCutButton
            v-if="canEndMatch"
            @click="endGame"
            variant="primary"
            cut-position="left-top"
            :loading="isEndGameLoading"
            :disabled="isEndGameLoading"
        >
            {{ $t('game_stats.end_game') }}
        </DiagonalCutButton>
        <DiagonalCutButton v-else @click="returnHome" variant="primary" cut-position="left-top">
            {{ $t('game_stats.return_home') }}
        </DiagonalCutButton>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { apiService } from '@/services/api.js'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import PageHeader from '@/components/PageHeader.vue'
import StatRow from '@/components/ui/StatRow.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import Avatar from '@/components/Avatar.vue'

export default {
    name: 'GameStatsView',
    components: { PageHeader, StatRow, DiagonalCutButton, Avatar },
    data() {
        return {
            matchData: null,
            matchStats: null,
            loading: true,
            error: null,
            isRematchLoading: false,
            isEndGameLoading: false,
        }
    },
    computed: {
        ...mapStores(useAuthStore),

        matchId() {
            return this.$route.params.matchId
        },
        teamGroups() {
            if (!this.matchData?.profiles) return {}

            const groups = {}
            this.matchData.profiles.forEach((p) => {
                if (!groups[p.team]) {
                    groups[p.team] = []
                }
                groups[p.team].push(p)
            })

            return groups
        },
        teams() {
            return Object.values(this.teamGroups)
        },
        isTeamGame() {
            return this.teams.some((team) => team.length > 1)
        },
        isKillerGame() {
            return this.matchData?.matchtype_id === 4
        },
        isX01Game() {
            return this.matchData?.matchtype?.processor_name === 'x01'
        },
        isSegmentsGame() {
            return this.matchData?.matchtype?.processor_name === 'segments'
        },
        isSegmentsPointsMode() {
            return this.matchData?.matchdata?.settings?.segment_mode == 1
        },
        isSoloGame() {
            return this.matchData?.profiles?.length === 1
        },
        x01Stats() {
            return [
                { key: 'average_throw', label: this.$t('game_stats.x01_avg'), format: 'float' },
                { key: 'first_9_average', label: this.$t('game_stats.x01_first_9_avg'), format: 'float' },
                { key: 'checkout_percentage', label: this.$t('game_stats.x01_checkout_pct'), format: 'percent' },
                { key: 'checkouts', label: this.$t('game_stats.x01_checkouts'), format: 'checkouts' },
                { key: 'highest_finish', label: this.$t('game_stats.x01_highest_finish'), format: 'integer' },
                { key: 'highest_score', label: this.$t('game_stats.x01_highest_score'), format: 'integer' },
                { key: 'best_leg', label: this.$t('game_stats.x01_best_leg'), format: 'darts', noHighlight: true },
                { key: 'worst_leg', label: this.$t('game_stats.x01_worst_leg'), format: 'darts', noHighlight: true },
            ]
        },
        segmentsStats() {
            if (this.isSegmentsPointsMode) {
                return [
                    { key: 'score', label: this.$t('game_stats.segments_score'), format: 'integer' },
                    { key: 'singles', label: this.$t('game_stats.segments_singles'), format: 'integer' },
                    { key: 'doubles', label: this.$t('game_stats.segments_doubles'), format: 'integer' },
                    { key: 'triples', label: this.$t('game_stats.segments_triples'), format: 'integer' },
                    { key: 'misses', label: this.$t('game_stats.segments_misses'), format: 'integer', lowerIsBetter: true },
                    { key: 'total_darts_thrown', label: this.$t('game_stats.segments_darts_thrown'), format: 'integer', lowerIsBetter: true },
                    { key: 'accuracy', label: this.$t('game_stats.segments_accuracy'), format: 'percent' },
                ]
            }
            return [
                { key: 'segments_completed', label: this.$t('game_stats.segments_cleared'), format: 'segments' },
                { key: 'misses', label: this.$t('game_stats.segments_misses'), format: 'integer', lowerIsBetter: true },
                { key: 'total_darts_thrown', label: this.$t('game_stats.segments_darts_thrown'), format: 'integer', lowerIsBetter: true },
                { key: 'accuracy', label: this.$t('game_stats.segments_accuracy'), format: 'percent' },
            ]
        },
        tableStats() {
            if (this.isX01Game) return this.x01Stats
            if (this.isSegmentsGame) return this.segmentsStats
            return this.availableStats
        },
        killerStats() {
            if (!this.isKillerGame || !this.matchData?.matchdata?.sets) return {}

            const stats = {}
            const sets = this.matchData.matchdata.sets

            if (sets.length === 0) return stats

            const lastSet = sets[sets.length - 1]
            if (!lastSet.legs || lastSet.legs.length === 0) return stats

            const lastLeg = lastSet.legs[lastSet.legs.length - 1]
            if (!lastLeg.turns || lastLeg.turns.length === 0) return stats

            const lastTurn = lastLeg.turns[lastLeg.turns.length - 1]
            if (!lastTurn.team_states) return stats

            lastTurn.team_states.forEach((state) => {
                stats[state.team] = {
                    lives: Math.max(0, state.lives),
                    isKiller: state.is_killer,
                    hitsOnTarget: state.hits_on_own_target,
                    eliminated: state.lives <= 0,
                }
            })

            return stats
        },
        isCreator() {
            return this.matchData?.creator_profile_id === this.authStore.profile?.id
        },
        isMatchRecentlyEnded() {
            if (!this.matchData?.finished_at) return false

            const finishedAt = new Date(this.matchData.finished_at)
            const now = new Date()
            const oneMinuteAgo = new Date(now.getTime() - 60 * 1000) // 60 seconds ago

            return finishedAt > oneMinuteAgo
        },
        canEndMatch() {
            return this.isCreator && this.isMatchRecentlyEnded
        },
        availableStats() {
            const stats = []
            const allTeamStats = Object.values(this.matchStats?.statistics || {})
            const allStatKeys = new Set(
                allTeamStats.flatMap((teamStats) => Object.keys(teamStats || {})),
            )

            if (allStatKeys.has('average_throw')) {
                stats.push({ key: 'average_throw', label: this.$t('game_stats.stat_average_throw') })
            }
            if (allStatKeys.has('first_9_average')) {
                stats.push({ key: 'first_9_average', label: this.$t('game_stats.stat_first_9_average') })
            }
            if (allStatKeys.has('double_percentage')) {
                stats.push({ key: 'double_percentage', label: this.$t('game_stats.stat_double_pct') })
            }
            if (allStatKeys.has('180')) {
                stats.push({ key: '180', label: this.$t('game_stats.stat_180s') })
            }
            if (allStatKeys.has('177')) {
                stats.push({ key: '177', label: this.$t('game_stats.stat_177s') })
            }
            if (allStatKeys.has('140_plus')) {
                stats.push({ key: '140_plus', label: this.$t('game_stats.stat_140_plus') })
            }
            if (allStatKeys.has('100_plus')) {
                stats.push({ key: '100_plus', label: this.$t('game_stats.stat_100_plus') })
            }

            const hasCricketStats = Array.from(allStatKeys).some(
                (key) => key.startsWith('triple_') || key.startsWith('double_'),
            )
            if (hasCricketStats) {
                if (stats.length > 0) {
                    stats.push({ key: 'spacer', label: '', isSpacer: true })
                }

                for (let i = 20; i >= 15; i--) {
                    const tripleKey = `triple_${i}`
                    if (allStatKeys.has(tripleKey)) {
                        stats.push({ key: tripleKey, label: this.$t('game_stats.stat_triple', { n: i }) })
                    }
                }

                const hasDoubles = Array.from(allStatKeys).some((key) => key.startsWith('double_'))
                if (stats.length > 0 && hasDoubles) {
                    stats.push({ key: 'spacer', label: '', isSpacer: true })
                }

                for (let i = 20; i >= 15; i--) {
                    const doubleKey = `double_${i}`
                    if (allStatKeys.has(doubleKey)) {
                        stats.push({ key: doubleKey, label: this.$t('game_stats.stat_double', { n: i }) })
                    }
                }
            }

            return stats
        },
    },
    async mounted() {
        if (!this.matchId) {
            this.error = this.$t('game_stats.error_no_match_id')
            this.loading = false
            return
        }
        await this.fetchMatchData()
    },
    methods: {
        async fetchMatchData() {
            try {
                this.loading = true
                this.error = null

                const [matchResponse, statsResponse] = await Promise.all([
                    apiService.get(`/api/v1/matches/${this.matchId}`),
                    apiService.get(`/api/v1/matches/${this.matchId}/stats`),
                ])

                this.matchData = matchResponse.match || matchResponse
                this.matchStats = statsResponse.stats || statsResponse
            } catch (error) {
                console.error('Error fetching match data:', error)
                this.error = error.message || this.$t('game_stats.error_load_stats')
            } finally {
                this.loading = false
            }
        },
        goBack() {
            this.$router.replace({ name: 'home' })
        },
        async rematch() {
            if (this.isRematchLoading) return

            this.isRematchLoading = true
            try {
                const matchStore = useMatchStore()
                const match = await matchStore.rematch(this.matchId)

                const isSoloGame = matchStore.getProfiles.length === 1

                if (isSoloGame) {
                    const soloPlayer = matchStore.getProfiles[0]
                    matchStore.setup.starting_player = soloPlayer.team

                    await matchStore.startMatch()
                    this.$router.push({
                        name: 'match-play',
                        params: { matchId: match.id },
                    })
                } else {
                    this.$router.push({
                        name: 'match-starter-selector',
                        params: { matchId: match.id },
                    })
                }
            } catch (error) {
                console.error('Failed to create rematch:', error)
                this.$toast(
                    this.$t('general.error'),
                    5000,
                    this.$t('general.error_retry'),
                )
                this.isRematchLoading = false
            }
        },
        async endGame() {
            if (this.isEndGameLoading) return

            this.isEndGameLoading = true
            try {
                const matchStore = useMatchStore()
                await matchStore.endMatch(this.matchId)
            } catch (error) {
                console.error('Failed to end match:', error)
                // Silently fail - continue to home page anyway
            }

            this.$router.push({ name: 'home' })
        },
        returnHome() {
            this.$router.push({ name: 'home' })
        },
        isWinner(playerId) {
            if (!playerId || !this.matchData?.matchdata?.winner_id) return false
            return this.matchData.matchdata.winner_id === playerId
        },

        getStatValue(playerId, key) {
            if (!playerId || !this.matchStats?.statistics) return null
            const value = this.matchStats.statistics[playerId]?.[key]
            if (value === null || value === undefined) return null
            return value
        },
        formatX01Stat(value, stat, playerId) {
            if (stat.key === 'checkouts') {
                const successful = this.matchStats?.statistics?.[playerId]?.checkout_successful
                const attempts = this.matchStats?.statistics?.[playerId]?.checkout_attempts
                if (successful == null && attempts == null) return null
                return `${successful ?? 0}/${attempts ?? 0}`
            }
            if (value === null || value === undefined) return null
            switch (stat.format) {
                case 'float':
                    return typeof value === 'number' ? value.toFixed(2) : value
                case 'percent':
                    return typeof value === 'number' ? `${value.toFixed(2)}%` : value
                case 'integer':
                    return Number.isInteger(value) ? value : Math.round(value)
                case 'darts':
                    return Number.isInteger(value) ? `${value} ${this.$t('game_stats.x01_darts_suffix')}` : value
                default:
                    return value
            }
        },
        formatSegmentsStat(value, stat, playerId) {
            if (stat.key === 'segments_completed') {
                const completed = this.getStatValue(playerId, 'segments_completed')
                const total = this.getStatValue(playerId, 'segments_total')
                if (completed == null && total == null) return null
                return `${completed ?? 0}/${total ?? 0}`
            }
            if (value === null || value === undefined) return null
            switch (stat.format) {
                case 'percent':
                    return typeof value === 'number' ? `${value}%` : value
                case 'integer':
                    return Number.isInteger(value) ? value : Math.round(value)
                default:
                    return value
            }
        },
        formatDoublePercentage(value) {
            if (value === null || value === undefined) return this.$t('general.not_available')
            return value
        },
    },
}
</script>
